// Payment Request Service
import { supabase } from '../lib/supabase';

const toNumber = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

const paymentRequestService = {
  // ─── Payment Requests ───────────────────────────────────────────────────────

  async getByProjectId(projectId) {
    const { data, error } = await supabase
      ?.from('payment_requests')
      ?.select(`
        *,
        period_start,
        period_end,
        updated_by,
        updated_at,
        updater:user_profiles!payment_requests_updated_by_fkey(id, full_name)
      `)
      ?.eq('project_id', projectId)
      ?.order('number', { ascending: true });
    if (error) throw error;
    return (data || [])?.map(row => ({
      ...row,
      updater_name: row?.updater?.full_name || null,
    }));
  },

  async getById(requestId) {
    const { data, error } = await supabase?.from('payment_requests')?.select('*, period_start, period_end, retention_rate')?.eq('id', requestId)?.single();
    if (error) throw error;
    return data;
  },

  async getNextNumber(projectId) {
    const { data, error } = await supabase?.from('payment_requests')?.select('number')?.eq('project_id', projectId)?.order('number', { ascending: false })?.limit(1);
    if (error) throw error;
    return data?.length > 0 ? data?.[0]?.number + 1 : 1;
  },

  async create(projectId, status = 'draft', periodStart = null, periodEnd = null, retentionRate = 10) {
    const number = await paymentRequestService?.getNextNumber(projectId);
    const { data: { user } } = await supabase?.auth?.getUser();
    const userId = user?.id || null;
    const { data, error } = await supabase
      ?.from('payment_requests')
      ?.insert({
        project_id: projectId,
        number,
        status,
        period_start: periodStart,
        period_end: periodEnd,
        retention_rate: retentionRate,
        updated_by: userId,
      })
      ?.select()
      ?.single();
    if (error) throw error;
    return data;
  },

  // `extra` : colonnes ecrites avec le statut (paid_at, paid_amount pour « Paiement reçu »).
  async updateStatus(requestId, status, extra = {}) {
    const { data: { user } } = await supabase?.auth?.getUser();
    const userId = user?.id || null;
    const { data, error } = await supabase
      ?.from('payment_requests')
      ?.update({ status, ...extra, updated_by: userId })
      ?.eq('id', requestId)
      ?.select()
      ?.single();
    if (error) throw error;
    return data;
  },

  // Suivi de facturation, separe du statut d'approbation.
  // `extra` : invoice_number, invoice_date (« Facturée ») ou paid_at, paid_amount (« Payée »).
  async updateBillingStatus(requestId, billingStatus, extra = {}) {
    const { data: { user } } = await supabase?.auth?.getUser();
    const userId = user?.id || null;
    const { data, error } = await supabase
      ?.from('payment_requests')
      ?.update({ billing_status: billingStatus, ...extra, updated_by: userId })
      ?.eq('id', requestId)
      ?.select()
      ?.single();
    if (error) throw error;
    return data;
  },

  async updatePeriod(requestId, periodStart, periodEnd) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      const userId = user?.id || null;
      const { data, error } = await supabase
        ?.from('payment_requests')
        ?.update({ period_start: periodStart, period_end: periodEnd, updated_by: userId })
        ?.eq('id', requestId)
        ?.select()
        ?.single();
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async updateRetentionRate(requestId, retentionRate) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      const userId = user?.id || null;
      const { data, error } = await supabase
        ?.from('payment_requests')
        ?.update({ retention_rate: retentionRate, updated_by: userId })
        ?.eq('id', requestId)
        ?.select()
        ?.single();
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async delete(requestId) {
    // Step 1: Delete all payment_values rows for this request
    const { error: valuesError } = await supabase
      ?.from('payment_values')
      ?.delete()
      ?.eq('request_id', requestId);
    if (valuesError) throw valuesError;

    // Step 2: Delete payment_items exclusively owned by this request
    // (created_in_request_id = requestId AND no payment_values row references
    //  the item for any OTHER request)
    const { data: candidateItems, error: itemsSelectError } = await supabase
      ?.from('payment_items')
      ?.select('id')
      ?.eq('created_in_request_id', requestId);
    if (itemsSelectError) throw itemsSelectError;

    if (candidateItems && candidateItems?.length > 0) {
      const candidateIds = candidateItems?.map(i => i?.id);

      // Find which of those items still have payment_values in OTHER requests
      const { data: sharedValues, error: sharedError } = await supabase
        ?.from('payment_values')
        ?.select('item_id')
        ?.in('item_id', candidateIds);
      if (sharedError) throw sharedError;

      const sharedItemIds = new Set((sharedValues || []).map(v => v.item_id));
      const exclusiveIds = candidateIds?.filter(id => !sharedItemIds?.has(id));

      if (exclusiveIds?.length > 0) {
        const { error: itemsDeleteError } = await supabase
          ?.from('payment_items')
          ?.delete()
          ?.in('id', exclusiveIds);
        if (itemsDeleteError) throw itemsDeleteError;
      }
    }

    // Step 3: Delete the payment_requests row itself
    const { error: requestError } = await supabase
      ?.from('payment_requests')
      ?.delete()
      ?.eq('id', requestId);
    if (requestError) throw requestError;

    return true;
  },

  async getLinkedCostCounts(requestId) {
    const [invoiceResult, expenseResult] = await Promise.all([
      supabase
        ?.from('sub_invoices')
        ?.select('id', { count: 'exact', head: true })
        ?.eq('payment_request_id', requestId),
      supabase
        ?.from('project_expenses')
        ?.select('id', { count: 'exact', head: true })
        ?.eq('payment_request_id', requestId),
    ]);
    return {
      invoices: invoiceResult?.count ?? 0,
      expenses: expenseResult?.count ?? 0,
    };
  },

  // ─── Payment Items ───────────────────────────────────────────────────────────

  async getItemsByProjectId(projectId) {
    const { data, error } = await supabase
      ?.from('payment_items')
      ?.select('*, project_extras(co_number)')
      ?.eq('project_id', projectId)
      ?.order('created_at', { ascending: true });
    if (error) throw error;
    return (data || [])?.map(item => ({
      ...item,
      extra_code: item?.project_extras?.co_number || null,
    }));
  },

  async createItem(projectId, requestId, description, contractAmount, isExtra = false, linkedExtraId = null) {
    const { data, error } = await supabase?.from('payment_items')?.insert({
        project_id: projectId,
        description,
        contract_amount: contractAmount,
        is_extra: isExtra,
        created_in_request_id: requestId,
        linked_extra_id: linkedExtraId || null,
      })?.select()?.single();
    if (error) throw error;
    return data;
  },

  async updateItem(itemId, updates) {
    const { data, error } = await supabase?.from('payment_items')?.update(updates)?.eq('id', itemId)?.select()?.single();
    if (error) throw error;
    return data;
  },

  async deleteItem(itemId) {
    const { error } = await supabase?.from('payment_items')?.delete()?.eq('id', itemId);
    if (error) throw error;
    return true;
  },

  // ─── Payment Values ──────────────────────────────────────────────────────────

  async getValuesByRequestId(requestId) {
    const { data, error } = await supabase?.from('payment_values')?.select('*, payment_items(*)')?.eq('request_id', requestId);
    if (error) throw error;
    return data || [];
  },

  async upsertValue(requestId, itemId, previousAmount, currentAmount) {
    const monthlyAmount = currentAmount - previousAmount;
    const { data, error } = await supabase?.from('payment_values')?.upsert(
        {
          request_id: requestId,
          item_id: itemId,
          previous_amount: previousAmount,
          current_amount: currentAmount,
          monthly_amount: monthlyAmount,
        },
        { onConflict: 'request_id,item_id' }
      )?.select()?.single();
    if (error) throw error;
    return data;
  },

  // Get the last current_amount for an item (from the most recent request before a given request number)
  async getPreviousCurrentAmount(projectId, itemId, beforeRequestNumber) {
    // Get all requests for this project ordered by number
    const { data: requests, error: reqError } = await supabase?.from('payment_requests')?.select('id, number')?.eq('project_id', projectId)?.lt('number', beforeRequestNumber)?.order('number', { ascending: false })?.limit(1);
    if (reqError) throw reqError;
    if (!requests || requests?.length === 0) return 0;

    const prevRequestId = requests?.[0]?.id;
    const { data: val, error: valError } = await supabase?.from('payment_values')?.select('current_amount')?.eq('request_id', prevRequestId)?.eq('item_id', itemId)?.maybeSingle();
    if (valError) throw valError;
    return val?.current_amount ?? 0;
  },

  // Full save: create/update request + items + values atomically
  async saveRequest(projectId, requestId, requestNumber, items, extraItems, status = 'draft') {
    // 1. Update request status + updated_by
    const { data: { user } } = await supabase?.auth?.getUser();
    const userId = user?.id || null;
    const { error: reqError } = await supabase
      ?.from('payment_requests')
      ?.update({ status, updated_by: userId })
      ?.eq('id', requestId);
    if (reqError) throw reqError;

    const isDP1 = requestNumber === 1;

    // 2. Process all items (base + extras)
    const allItems = [...items, ...extraItems];
    for (const item of allItems) {
      let dbItemId = item?.id;

      if (!dbItemId) {
        // New item — create it
        const created = await paymentRequestService?.createItem(
          projectId,
          requestId,
          item?.description,
          toNumber(item?.contractAmount),
          item?.isExtra || false,
          item?.linkedExtraId || null
        );
        dbItemId = created?.id;
        item.id = dbItemId;
      } else if (isDP1) {
        // DP1: can update description and contract_amount
        await paymentRequestService?.updateItem(dbItemId, {
          description: item?.description,
          contract_amount: toNumber(item?.contractAmount),
        });
      }

      // Upsert value
      let previousAmount = isDP1 ? 0 : toNumber(item?.previousAmount);
      const currentAmount = toNumber(item?.currentAmount);
      const monthlyAmount = toNumber(currentAmount - previousAmount);
      await paymentRequestService?.upsertValue(requestId, dbItemId, previousAmount, currentAmount);
    }

    return true;
  },

  // Load full request data (items + values) for editing
  async loadRequestData(projectId, requestId, requestNumber) {
    const items = await paymentRequestService?.getItemsByProjectId(projectId);
    const values = await paymentRequestService?.getValuesByRequestId(requestId);
    const requestRow = await paymentRequestService?.getById(requestId);

    const valueMap = {};
    values?.forEach(v => { valueMap[v.item_id] = v; });

    const baseItems = [];
    const extraItems = [];

    for (const item of items) {
      const val = valueMap?.[item?.id];
      const isDP1 = requestNumber === 1;

      let previousAmount = 0;
      if (!isDP1) {
        previousAmount = toNumber(val?.previous_amount ?? (await paymentRequestService?.getPreviousCurrentAmount(projectId, item?.id, requestNumber)));
      }

      const enriched = {
        id: item?.id,
        description: item?.description,
        contractAmount: toNumber(item?.contract_amount),
        isExtra: item?.is_extra,
        previousAmount,
        currentAmount: toNumber(val?.current_amount),
        monthlyAmount: toNumber(val?.monthly_amount),
        linkedExtraId: item?.linked_extra_id || null,
        retention_rate: requestRow?.retention_rate ?? 10,
      };

      if (item?.is_extra) {
        extraItems?.push(enriched);
      } else {
        baseItems?.push(enriched);
      }
    }

    return { baseItems, extraItems, retention_rate: requestRow?.retention_rate ?? 10 };
  },
};

export default paymentRequestService;
